import { useEffect } from "react";

const SITE = "https://laharipackersandmovers.in";
const DEFAULT_IMAGE =
  "https://readdy.ai/api/search-image?query=Wide%20cinematic%20banner%20of%20a%20professional%20moving%20crew%20in%20uniform%20loading%20neatly%20wrapped%20cardboard%20boxes%20and%20furniture%20into%20a%20clean%20modern%20moving%20truck%20parked%20outside%20a%20bright%20Bangalore%20residential%20apartment%20building%2C%20warm%20golden%20hour%20light%2C%20deep%20green%20and%20cream%20color%20palette%2C%20clear%20uncluttered%20composition%2C%20photorealistic%20premium%20relocation%20marketing%20photography%2C%20no%20text%20letters%20or%20logos&width=1200&height=630&seq=lahari-social-cover-2026&orientation=landscape";
const SOCIAL_IMAGE_ALT = "Lahari Packers and Movers Bangalore - home and office shifting service";

/** Ensure the preview image is a full web address — social crawlers cannot resolve relative paths. */
const toAbsoluteUrl = (image: string): string => {
  if (/^https?:\/\//i.test(image)) return image;
  return `${SITE}${image.startsWith("/") ? "" : "/"}${image}`;
};

interface PageMetaProps {
  title: string;
  description?: string;
  /** Route path for canonical + og:url, e.g. "/services/house-shifting". Falls back to the current path. */
  path?: string;
  image?: string;
  type?: string;
}

const resolveUrl = (path?: string): string => {
  if (path) return path === "/" ? `${SITE}/` : `${SITE}${path}`;
  if (typeof window === "undefined") return `${SITE}/`;
  return `${SITE}${window.location.pathname}`;
};

const truncate = (text: string, max = 155): string => {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
};

interface MetaTag {
  attr: "name" | "property";
  key: string;
  content: string;
}

/**
 * Sets per-page document title, meta description, canonical URL and
 * Open Graph / Twitter preview tags. Values are restored on unmount so
 * the static tags in index.html remain the default for pages without it.
 */
export default function PageMeta({ title, description, path, image, type = "website" }: PageMetaProps) {
  useEffect(() => {
    const prevTitle = document.title;
    const url = resolveUrl(path);
    const desc = description ? truncate(description) : "";
    const img = toAbsoluteUrl(image || DEFAULT_IMAGE);

    document.title = title;

    const tags: MetaTag[] = [
      { attr: "name", key: "description", content: desc },
      { attr: "property", key: "og:title", content: title },
      { attr: "property", key: "og:description", content: desc },
      { attr: "property", key: "og:type", content: type },
      { attr: "property", key: "og:url", content: url },
      { attr: "property", key: "og:site_name", content: "Lahari Packers and Movers, Bangalore" },
      { attr: "property", key: "og:locale", content: "en_IN" },
      { attr: "property", key: "og:image", content: img },
      { attr: "property", key: "og:image:alt", content: SOCIAL_IMAGE_ALT },
      { attr: "name", key: "twitter:card", content: "summary_large_image" },
      { attr: "name", key: "twitter:title", content: title },
      { attr: "name", key: "twitter:description", content: desc },
      { attr: "name", key: "twitter:image", content: img },
      { attr: "name", key: "twitter:image:alt", content: SOCIAL_IMAGE_ALT },
    ];

    const created: HTMLMetaElement[] = [];
    const restored: { el: HTMLMetaElement; previous: string | null }[] = [];

    tags.forEach(({ attr, key, content }) => {
      if (!content) return;
      const selector = `meta[${attr}="${key}"]`;
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (el) {
        restored.push({ el, previous: el.getAttribute("content") });
      } else {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", content);
    });

    const canonicalSelector = 'link[rel="canonical"]';
    let canonical = document.head.querySelector<HTMLLinkElement>(canonicalSelector);
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    canonical.setAttribute("href", url);

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
      restored.forEach(({ el, previous }) => {
        if (previous === null) el.removeAttribute("content");
        else el.setAttribute("content", previous);
      });
      if (createdCanonical && canonical) canonical.remove();
      else if (canonical && prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
    };
  }, [title, description, path, image, type]);

  return null;
}